import { useEffect, useRef } from 'react'
import type { GuestProfile } from '../api/types'
import { plural } from '../lib/format'
import { ThemeChoice } from './ThemeChoice'

/**
 * Het menu onder de avatar (ronde 6C) en boven de zijnavigatie (ronde 7A).
 * Zelfde inhoud, andere ankerpunt — dat verschil zit in `anchor`.
 */
export function GuestMenu({
  profile,
  displayName,
  anchor,
  readOnly = false,
  onEditName,
  onOpenUploads,
  onSignOut,
  onClose,
}: {
  profile: GuestProfile | undefined
  displayName: string
  anchor: 'top-right' | 'bottom-left'

  /**
   * Meekijken zonder mee te doen: de eigenaar in een privé galerij. Dan gaat
   * elk item hier over iets wat hij niet heeft — een naam, uploads, een
   * gast-sessie om uit te loggen — en blijft de weg terug naar het beheer over.
   * De weergavekeuze blijft wel staan: die hoort bij de browser, niet bij wie je
   * bent.
   */
  readOnly?: boolean

  onEditName: () => void
  onOpenUploads: () => void
  onSignOut: () => void
  onClose: () => void
}) {
  const menu = useRef<HTMLDivElement>(null)

  // Buiten klikken sluit. In de capture-fase, anders sluit het menu pas nadat
  // de klik al iets achterliggends heeft aangeraakt.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element | null

      // Een druk ín een menu is niet "buiten": op elk scherm staan er twee, een
      // voor mobiel en een voor desktop, met één gedeelde open-stand. Ze zijn
      // allebei gemonteerd en luisteren allebei mee, maar één is met CSS
      // verborgen. Keek elk menu alleen naar zichzelf, dan zag die verborgen
      // tweeling een druk in de zichtbare als "buiten mij" en sloot hij alles —
      // op pointerdown, dus nog voor de klik bij de knop aankwam. Uitloggen
      // deed daardoor niets.
      //
      // De knop die het menu opent telt ook niet mee. Zou die wel meetellen,
      // dan sluit het menu bij het indrukken en opent het bij de klik erna, en
      // is het met dezelfde knop niet meer dicht te krijgen.
      if (target?.closest('[role="menu"], [data-menu-anchor]')) {
        return
      }

      onClose()
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  const position = anchor === 'top-right' ? 'top-19 right-4 w-62.5' : 'bottom-13 left-0 w-61'

  return (
    <>
      {/* Het waas over de rest van het scherm, zoals in ronde 6C. */}
      <div className="bg-shell/55 fixed inset-0 z-30" aria-hidden="true" />

      <div
        ref={menu}
        role="menu"
        className={`rounded-card border-line-card bg-surface shadow-menu absolute z-40 border p-1.5 text-left ${position}`}
      >
        <div className="border-line-menu border-b px-3.5 pt-3 pb-3.5">
          <p className="text-base font-semibold">{displayName}</p>
          <p className="text-ink-faint mt-0.75 text-[13px]">
            {readOnly
              ? 'Alleen bekijken · deze galerij is privé'
              : profile
                ? `${plural(profile.uploadCount, 'upload', 'uploads')} · ${plural(profile.challengeCount, 'challenge', 'challenges')}`
                : 'Tellen…'}
          </p>
        </div>

        <ThemeChoice />

        <div className="flex flex-col pt-1">
          {readOnly ? (
            <MenuItem icon="←" tone="accent" onClick={() => (window.location.href = '/beheer')}>
              Naar beheer
            </MenuItem>
          ) : (
            <>
              <MenuItem icon="✎" onClick={onEditName}>
                Naam aanpassen
              </MenuItem>

              <MenuItem icon="◷" onClick={onOpenUploads}>
                Uploadstatus
              </MenuItem>

              <MenuItem icon="→" tone="accent" separated onClick={onSignOut}>
                Uitloggen
              </MenuItem>
            </>
          )}
        </div>
      </div>
    </>
  )
}

function MenuItem({
  icon,
  children,
  onClick,
  tone = 'default',
  separated = false,
}: {
  icon: string
  children: string
  onClick: () => void
  tone?: 'default' | 'accent'
  separated?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        'hover:bg-tint/50 flex items-center gap-3 rounded-lg px-3.5 py-3.25 text-left text-[15px] ' +
        (separated ? 'border-line-menu mt-1 border-t ' : '') +
        (tone === 'accent' ? 'text-accent' : '')
      }
    >
      <span
        aria-hidden="true"
        className={'w-4.5 text-center ' + (tone === 'accent' ? '' : 'text-ink-soft')}
      >
        {icon}
      </span>
      {children}
    </button>
  )
}
